import React, { useEffect, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Address } from "../../models/base";
import { UseFormSetValue } from "react-hook-form";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";

interface MapComponentProps {
  userLocation: Address;
  setValue: UseFormSetValue<any>;
  canSetLocation?: boolean;
}

function MapComponent ({userLocation, setValue, canSetLocation}: MapComponentProps) {

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [markerPosition, setMarkerPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const fetchCoordinates = async (address: string) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${googleMapsApiKey}`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
      } else {
        console.error("No results found for the provided address.");
      }
    } catch (error) {
      console.error("Error fetching coordinates: ", error);
    }
  };

  const getFormattedAddress = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}&language=en`
      );

      const data = await response.json();
      // console.log(data);

      if (data.results && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;

        const formattedAddressParts = addressComponents
          .filter(
            (component: { types: string[] }) =>
              !component.types.includes("plus_code")
          )
          .map((component: { long_name: string }) => component.long_name);

        const formattedAddress = formattedAddressParts.join(", ");
        setValue("address.address", formattedAddress);
        setValue("address.coordinates", {lat: lat, lon: lng });
        console.log("Formatted Address:", formattedAddress, markerPosition);

        return formattedAddress;
      } else {
        console.error("No results found for the provided coordinates.");
        return null;
      }
    } catch (error) {
      console.error("Error fetching address: ", error);
      return null;
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenter({ lat: latitude, lng: longitude });
          setMarkerPosition({ lat: latitude, lng: longitude });
        },
        (error) => {
          setCenter({lat: 40.7128, lng: -74.006});
          setMarkerPosition({lat: 40.7128, lng: -74.006});
        }
      );
    }
  };

  useEffect(() => {
    if (userLocation && userLocation.address) {
      fetchCoordinates(userLocation.address);
    } else {
      getCurrentLocation();
    }
  }, [userLocation]);

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (canSetLocation === true && event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMarkerPosition({ lat, lng });
      getFormattedAddress(lat, lng);
    }
  };

  if (!center || !markerPosition) {
    return <div><BaseLoadingSpinner/></div>;
  }

  return (
    <GoogleMap
      center={center}
      zoom={15}
      mapTypeId={"satellite"}
      options={{
        disableDefaultUI: true,
      }}
      mapContainerStyle={{
        width: "100%",
        height: "350px",
      }}
      onClick={handleMapClick}
    >
      <Marker position={markerPosition} />
    </GoogleMap>
  );
};

export default MapComponent;
